import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { ArrowLeft, Download, Eye, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/** platformLedger doc id → units/{unitId}/creditTransactions doc id */
function unitCreditTransactionIdFromPlatformLedger(platformDocId: string): string {
  if (platformDocId.startsWith('stripe_credit_sale:')) {
    const sessionId = platformDocId.slice('stripe_credit_sale:'.length);
    return `stripe_credit:${sessionId}`;
  }
  if (platformDocId.startsWith('slickpay_ob_credit_sale:')) {
    const poId = platformDocId.slice('slickpay_ob_credit_sale:'.length);
    return `slickpay_ob_credit:${poId}`;
  }
  return platformDocId;
}

function providerLabel(provider: string | undefined): string {
  if (!provider) return '—';
  if (provider === 'slickpay_ob') return 'Open Banking';
  if (provider === 'stripe') return 'Stripe';
  return provider;
}

function formatGbpPence(pence: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);
}

function formatFirestoreDate(value: unknown): string {
  if (value instanceof Timestamp) {
    return format(value.toDate(), 'dd MMM yyyy HH:mm');
  }
  if (value && typeof value === 'object' && 'toDate' in value && typeof (value as Timestamp).toDate === 'function') {
    return format((value as Timestamp).toDate(), 'dd MMM yyyy HH:mm');
  }
  return '—';
}

export type PlatformCreditSaleRow = {
  id: string;
  type?: string;
  createdAt?: Timestamp | null;
  date?: Timestamp | null;
  amountPence?: number;
  unitId: string;
  packId?: string;
  credits?: number;
  paymentProvider?: string;
  providerPaymentId?: string;
  providerPaymentReference?: string;
  status?: string;
};

type UnitMember = { uid?: string; role?: string; displayName?: string; email?: string };

export default function CreditsReportPage() {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<PlatformCreditSaleRow[]>([]);
  const [unitLabels, setUnitLabels] = useState<Record<string, string>>({});
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSale, setDetailSale] = useState<PlatformCreditSaleRow | null>(null);
  const [detailUnit, setDetailUnit] = useState<Record<string, unknown> | null>(null);
  const [detailUnitTx, setDetailUnitTx] = useState<Record<string, unknown> | null>(null);
  const [financeContacts, setFinanceContacts] = useState<{ uid: string; displayName: string; email: string; role: string }[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadSales = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'platformLedger'),
        where('type', '==', 'credit_sale'),
        orderBy('createdAt', 'desc'),
        limit(200),
      );
      const snap = await getDocs(q);
      const list: PlatformCreditSaleRow[] = [];
      snap.forEach((d) => {
        const data = d.data() as PlatformCreditSaleRow;
        list.push({ ...(data as object), id: d.id } as PlatformCreditSaleRow);
      });
      setRows(list);

      const uniqueUnitIds = [...new Set(list.map((r) => r.unitId).filter(Boolean))].slice(0, 120);
      const labelEntries = await Promise.all(
        uniqueUnitIds.map(async (uid) => {
          const us = await getDoc(doc(db, 'units', uid));
          const name = us.exists() ? String((us.data() as { name?: string }).name || uid) : uid;
          return [uid, name] as const;
        }),
      );
      setUnitLabels(Object.fromEntries(labelEntries));
    } catch (e) {
      console.error(e);
      showError('Could not load credit sales. If this is a new query, deploy the Firestore composite index for platformLedger (type + createdAt).');
      setRows([]);
      setUnitLabels({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSales();
  }, [loadSales]);

  const handleDownloadReport = async () => {
    setGenerating(true);
    try {
      const toEnd = new Date();
      toEnd.setHours(23, 59, 59, 999);
      const fromStart = new Date();
      fromStart.setDate(fromStart.getDate() - 90);
      fromStart.setHours(0, 0, 0, 0);
      const fn = httpsCallable(functions, 'generatePlatformIncomeReport');
      const res = await fn({
        fromDate: fromStart.toISOString(),
        toDate: toEnd.toISOString(),
      });
      const csvData = (res.data as { csv?: string }).csv;
      if (!csvData) {
        showError('Report returned no data');
        return;
      }

      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `platform-income-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('Report downloaded successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      showError('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const openDetails = async (sale: PlatformCreditSaleRow) => {
    setDetailSale(sale);
    setDetailUnit(null);
    setDetailUnitTx(null);
    setFinanceContacts([]);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const unitId = sale.unitId;
      const [unitSnap, txSnap] = await Promise.all([
        getDoc(doc(db, 'units', unitId)),
        getDoc(
          doc(db, 'units', unitId, 'creditTransactions', unitCreditTransactionIdFromPlatformLedger(sale.id)),
        ),
      ]);

      if (unitSnap.exists()) {
        setDetailUnit(unitSnap.data() as Record<string, unknown>);
        const members = (unitSnap.data()?.members as UnitMember[] | undefined) || [];
        const financeUids = members
          .filter((m) => {
            const r = (m.role || '').trim();
            return r === 'Secretary' || r === 'Treasurer';
          })
          .map((m) => (typeof m === 'object' && m.uid ? String(m.uid) : ''))
          .filter(Boolean);

        const unique = [...new Set(financeUids)];
        const contacts: { uid: string; displayName: string; email: string; role: string }[] = [];
        for (const uid of unique.slice(0, 24)) {
          const member = members.find((m) => m.uid === uid);
          const role = (member?.role || '').trim() || 'Member';
          const uSnap = await getDoc(doc(db, 'users', uid));
          if (uSnap.exists()) {
            const u = uSnap.data() as { displayName?: string; email?: string };
            contacts.push({
              uid,
              displayName: u.displayName || uid,
              email: u.email || '',
              role,
            });
          }
        }
        setFinanceContacts(contacts);
      } else {
        setDetailUnit(null);
      }

      setDetailUnitTx(txSnap.exists() ? (txSnap.data() as Record<string, unknown>) : null);
    } catch (e) {
      console.error(e);
      showError('Failed to load unit or transaction details');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Credits and sales reports</h1>
            <p className="text-muted-foreground">
              Recent credit pack purchases across the platform, with drill-down into each sale, unit, and finance
              contacts.
            </p>
          </div>

          <Card>
            <CardHeader className="flex flex-col gap-4 space-y-0 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1.5">
                <CardTitle>Recent credit sales</CardTitle>
                <CardDescription>
                  Up to 200 most recent confirmed credit pack purchases (newest first). Download a CSV of platform ledger
                  entries (credit sales and application fees) for the last 90 days for VAT and accounting.
                </CardDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleDownloadReport}
                disabled={generating}
                className="w-full shrink-0 sm:w-auto sm:self-start"
              >
                <Download className="mr-2 h-4 w-4" />
                {generating ? 'Generating…' : 'Download CSV report'}
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                  Loading…
                </div>
              ) : rows.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">No credit sales found.</p>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead className="text-right">Credits</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Provider</TableHead>
                        <TableHead className="w-[100px]"> </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((sale) => (
                        <TableRow key={sale.id}>
                          <TableCell className="whitespace-nowrap text-sm">
                            {formatFirestoreDate(sale.createdAt ?? sale.date)}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{unitLabels[sale.unitId] || sale.unitId}</div>
                            <div className="font-mono text-xs text-muted-foreground">{sale.unitId}</div>
                            {sale.packId ? (
                              <div className="text-xs text-muted-foreground">Pack: {sale.packId}</div>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-right">{sale.credits ?? '—'}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatGbpPence(Number(sale.amountPence ?? 0))}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{providerLabel(sale.paymentProvider)}</Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="outline" size="sm" onClick={() => void openDetails(sale)}>
                              <Eye className="mr-1 h-4 w-4" />
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent
          className="max-h-[90vh] max-w-3xl overflow-y-auto"
          onClose={() => {
            setDetailSale(null);
            setDetailUnit(null);
            setDetailUnitTx(null);
            setFinanceContacts([]);
          }}
        >
          <DialogHeader>
            <DialogTitle>Credit sale details</DialogTitle>
            <DialogDescription>
              Platform ledger entry, linked unit ledger transaction, unit profile, and secretary or treasurer contacts.
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-6 w-6 animate-spin" />
              Loading…
            </div>
          ) : detailSale ? (
            <div className="space-y-6 text-sm">
              <section>
                <h3 className="mb-2 font-semibold text-foreground">Purchase (platform ledger)</h3>
                <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <dt className="text-muted-foreground">Document ID</dt>
                    <dd className="font-mono text-xs break-all">{detailSale.id}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Recorded</dt>
                    <dd>{formatFirestoreDate(detailSale.createdAt ?? detailSale.date)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Unit ID</dt>
                    <dd className="font-mono text-xs break-all">{detailSale.unitId}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Amount</dt>
                    <dd>{formatGbpPence(Number(detailSale.amountPence ?? 0))}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Credits</dt>
                    <dd>{detailSale.credits ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Pack</dt>
                    <dd>{detailSale.packId || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Provider</dt>
                    <dd>{providerLabel(detailSale.paymentProvider)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Provider payment ID</dt>
                    <dd className="break-all">{detailSale.providerPaymentId || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Status</dt>
                    <dd>{detailSale.status || '—'}</dd>
                  </div>
                </dl>
              </section>

              <section>
                <h3 className="mb-2 font-semibold text-foreground">Unit ledger transaction</h3>
                {detailUnitTx ? (
                  <pre className="max-h-48 overflow-auto rounded-md bg-muted p-3 text-xs">
                    {JSON.stringify(
                      detailUnitTx,
                      (_, v) => (v instanceof Timestamp ? v.toDate().toISOString() : v),
                      2,
                    )}
                  </pre>
                ) : (
                  <p className="text-muted-foreground">No matching unit credit transaction found for this ledger ID.</p>
                )}
              </section>

              <section>
                <h3 className="mb-2 font-semibold text-foreground">Unit</h3>
                {detailUnit ? (
                  <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div>
                      <dt className="text-muted-foreground">Name</dt>
                      <dd>{String(detailUnit.name ?? '—')}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Number</dt>
                      <dd>{String(detailUnit.number ?? '—')}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Province</dt>
                      <dd>{String(detailUnit.province ?? '—')}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Credit balance (current)</dt>
                      <dd>{detailUnit.creditBalance != null ? String(detailUnit.creditBalance) : '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Review status</dt>
                      <dd>{String(detailUnit.reviewStatus ?? '—')}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Created</dt>
                      <dd>{formatFirestoreDate(detailUnit.createdAt)}</dd>
                    </div>
                  </dl>
                ) : (
                  <p className="text-muted-foreground">Unit document not found.</p>
                )}
              </section>

              <section>
                <h3 className="mb-2 font-semibold text-foreground">Finance contacts (Secretary / Treasurer)</h3>
                <p className="mb-2 text-xs text-muted-foreground">
                  Checkout does not store which user paid; these are unit officers who can purchase credits.
                </p>
                {financeContacts.length === 0 ? (
                  <p className="text-muted-foreground">No secretary or treasurer with accounts linked on this unit.</p>
                ) : (
                  <ul className="space-y-2">
                    {financeContacts.map((c) => (
                      <li key={c.uid} className="rounded-md border p-2">
                        <div className="font-medium">{c.displayName}</div>
                        <div className="text-xs text-muted-foreground">{c.role}</div>
                        {c.email ? <div className="text-xs">{c.email}</div> : null}
                        <div className="font-mono text-xs text-muted-foreground">{c.uid}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
